'use client';
import { Button, CTAItem, Left, Right, slugify, StaticImage } from 'mmds';
import { SwiperItem, SwiperProps } from './type';
import { SwiperSlide } from 'swiper/react';
import ScrollBarSwiper from '../../components/scrollbar-swiper';
import proxyImageUrlIfEnabled from '@app/lib/images/proxy-image-url-if-enabled';
import Linker from '@app/components/linker/linker';
import SquareSkeletonLoader from '@app/components/square-skeleton-loader/square-skeleton-loader';
import s from './Swiper.module.css';

const SwiperColVariant = ({ block, context }: SwiperProps) => {
    const { context: ContextText, items } = block;
    const itemsCheckForArrowVisibility = items.length > 3;
    const randomKey = Math.random().toString(36).substring(2, 15);
    const loaderSkeleton = (
        <>
            <div className={`${s.loader} ${s.loaderMobile}`}>
                <SquareSkeletonLoader
                    uniquekey={`swiper-loader-mobile-${randomKey}`}
                    height={600}
                    width={600}
                />
            </div>
            <div className={`${s.loader} ${s.loaderDesktop}`}>
                {items.map((item: SwiperItem, index) => (
                    <SquareSkeletonLoader
                        key={index}
                        uniquekey={`swiper-loader-${index}-${randomKey}`}
                        height={800}
                        width={600}
                    />
                ))}
            </div>
        </>
    );

    return (
        <div className={s.swiperRoot}>
            <div className={s.contextContainer}>
                <div className={s.context}>{ContextText}</div>
                {itemsCheckForArrowVisibility && (
                    <div className={s.iconContainer}>
                        <div className="swiper-button swiper-icon-prev-3">
                            <Left />
                        </div>
                        <div className="swiper-button swiper-icon-next-3">
                            <Right />
                        </div>
                    </div>
                )}
            </div>
            <ScrollBarSwiper
                className="swiper3"
                swiperIconNextClassName="swiper-icon-next-3"
                swiperIconPrevClassName="swiper-icon-prev-3"
                slidePerViewInDesktop={3.1}
                spaceBetweenDesktop={12}
                spaceBetweenMobile={8}
                slidePerViewInMobile={1.2}
                loadingSkeleton={loaderSkeleton}
            >
                {items.map((item: SwiperItem, index) => {
                    const {
                        desktopImage,
                        mobileImage,
                        overlayTitle,
                        overlayCTASet,
                        overlayContext,
                    } = item;
                    const identifier = `collection_swiper_${slugify(
                        ContextText
                    )}`;
                    const link = overlayCTASet?.[0]?.link;
                    return (
                        <SwiperSlide key={index} data-testid={identifier}>
                            <Linker
                                passthrough={!link}
                                href={link || null}
                                className={s.linkableWrapper}
                                title={`Go to page ${overlayTitle}`}
                                lang={context.lang}
                            >
                                <div className={s.swiperColContainer}>
                                    <div className={s.overlayOnImage} />
                                    <StaticImage
                                        desktopImage={desktopImage}
                                        mobileImage={mobileImage}
                                        fullWidth={true}
                                        urlTransformer={proxyImageUrlIfEnabled}
                                    />
                                    <div className={s.swiperColContent}>
                                        <div className={s.overlayContext}>
                                            {overlayContext}
                                        </div>
                                        <div className={s.overlayTitle}>
                                            {overlayTitle}
                                        </div>
                                        <div className={s.overlayButton}>
                                            {overlayCTASet?.map(
                                                (item: CTAItem, index) => {
                                                    return (
                                                        item.link && (
                                                            <span key={index}>
                                                                <Button
                                                                    variant={
                                                                        item.variant
                                                                    }
                                                                    label={
                                                                        item.label
                                                                    }
                                                                    icon={
                                                                        item.icon
                                                                    }
                                                                    className={
                                                                        s.linkableWrapperButton
                                                                    }
                                                                />
                                                            </span>
                                                        )
                                                    );
                                                }
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Linker>
                        </SwiperSlide>
                    );
                })}
            </ScrollBarSwiper>
        </div>
    );
};

export default SwiperColVariant;
